/**
 * AdminNotifications — compose & send push notifications to subscribers.
 *
 * Push notification support by platform:
 *   · Android Chrome / Firefox / Edge  — full support, works out of the box
 *   · iOS 16.4+ Safari                  — supported ONLY when the app was
 *     added to the Home Screen (installed as a PWA). We already prompt users
 *     to Add to Home Screen from PWAInstallPrompt.jsx.
 *   · Desktop Chrome / Firefox / Edge   — full support
 *   · Desktop Safari                    — supported (macOS 13+)
 *
 * Users first accept the browser permission, subscribe via the member portal
 * bell, and their subscription is stored in `push_subscriptions`. This page
 * sends one payload to every subscription that matches the audience.
 */
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, Users, User, Shield, Bell, Loader2, Globe } from "lucide-react";

const AUDIENCES = [
  { key: "everyone",           label: "Everyone",             icon: Globe,  hint: "Every reachable device — guests, PWA installers, members, parents, staff. The broadest bucket." },
  { key: "all",                label: "Everyone subscribed",  icon: Bell,   hint: "Signed-in users only (members + parents + staff). Excludes anonymous guests." },
  { key: "members",            label: "Facility members",     icon: User,   hint: "Members who booked / bought a plan." },
  { key: "parents",            label: "Parents / guardians",  icon: Users,  hint: "Parents of enrolled wards." },
  { key: "staff",              label: "Admin & Coaches",      icon: Shield, hint: "Internal team only." },
];

export default function AdminNotifications() {
  const [stats, setStats] = useState({ subscriptions: 0, unique_users: 0, recent_pushes: [] });
  const [form, setForm] = useState({ title: "", body: "", url: "/", audience: "everyone", require_interaction: false });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/push/subscription-stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and message are required"); return; }
    if (!window.confirm(`Send "${form.title}" to ${AUDIENCES.find((a) => a.key === form.audience)?.label}?\nThis cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/push/broadcast", form);
      toast.success(`Sent to ${data.sent} device${data.sent === 1 ? "" : "s"}${data.removed ? ` · ${data.removed} expired removed` : ""}.`);
      setForm({ ...form, title: "", body: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Communications · Push</div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Notifications.</h1>
        <p className="text-sm text-white/60 mt-3 max-w-3xl">
          Send a push notification to every browser / installed PWA that has enabled notifications on HTP Sports Hub. Delivered even when the site is closed. Works on Android, iOS PWAs (16.4+), and desktop Chrome / Firefox / Edge / Safari.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
        <Stat label="Active subscriptions" value={stats.subscriptions} sub="devices reachable"/>
        <Stat label="Unique users" value={stats.unique_users} sub="distinct people"/>
        <Stat label="Recent broadcasts" value={stats.recent_pushes?.length || 0} sub="last 20 sends"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Compose form */}
        <div className="md:col-span-2 border border-white/12 bg-[#131A2B] p-6 space-y-4">
          <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">// Compose</div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Title (max 65 chars)</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value.slice(0, 65) })}
              placeholder="Grand opening weekend — 20% off all bookings!"
              data-testid="notif-title"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm"/>
            <div className="text-right text-[10px] text-white/40 mt-1">{form.title.length}/65</div>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Message (max 240 chars)</label>
            <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value.slice(0, 240) })}
              rows={4}
              placeholder="Book any court this weekend and get 20% off — offer ends Sunday 10 PM. Tap to book."
              data-testid="notif-body"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm"/>
            <div className="text-right text-[10px] text-white/40 mt-1">{form.body.length}/240</div>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1">Deep link (where tap opens)</label>
            <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="/booking or /tournaments or https://…"
              data-testid="notif-url"
              className="w-full bg-black/40 border border-white/15 px-3 py-2 text-sm font-mono"/>
          </div>

          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">Audience</label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {AUDIENCES.map((a) => {
                const Icon = a.icon;
                const active = form.audience === a.key;
                return (
                  <button key={a.key} onClick={() => setForm({ ...form, audience: a.key })}
                    data-testid={`notif-aud-${a.key}`}
                    className={`p-3 text-left border ${active ? "border-brand-orange bg-brand-orange/10" : "border-white/15 hover:border-white/30"}`}>
                    <Icon size={14} className={active ? "text-brand-orange" : "text-white/60"}/>
                    <div className="text-xs font-semibold mt-1">{a.label}</div>
                    <div className="text-[10px] text-white/40 mt-0.5">{a.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
            <input type="checkbox" checked={form.require_interaction}
              onChange={(e) => setForm({ ...form, require_interaction: e.target.checked })}
              data-testid="notif-persistent"/>
            Sticky — notification stays until user taps or dismisses (Android + desktop only).
          </label>

          <button onClick={send} disabled={busy}
            data-testid="notif-send"
            className="w-full bg-brand-orange text-black hover:bg-white disabled:opacity-40 py-3 font-bold text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
            {busy ? "Sending…" : "Send Broadcast"}
          </button>
        </div>

        {/* Preview + history */}
        <div className="space-y-4">
          <div className="border border-brand-blue/40 bg-brand-blue/5 p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest brand-blue mb-2">// Preview</div>
            <div className="bg-black/60 border border-white/15 p-3 text-xs">
              <div className="flex items-start gap-2">
                <img
                  src="https://customer-assets-0z36b82j.emergentagent.net/job_court-booking-hub-22/artifacts/fsvri6nu_WhatsApp%20Image%202026-08-04%20at%2018.37.01.jpeg"
                  alt="HTP Sports Hub"
                  className="w-8 h-8 rounded-md shrink-0 object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline gap-2">
                    <div className="font-bold truncate">HTP Sports Hub</div>
                    <div className="text-[9px] text-white/40 shrink-0">now</div>
                  </div>
                  <div className="font-bold text-white mt-0.5 truncate">{form.title || "Notification title"}</div>
                  <div className="text-white/70 mt-0.5 line-clamp-2">{form.body || "Your message will appear here…"}</div>
                </div>
              </div>
            </div>
            <div className="text-[10px] text-white/40 mt-2">This is roughly how it&apos;ll look on Android / desktop. iOS shows a slightly different style.</div>
          </div>

          <div className="border border-white/12 bg-[#131A2B] p-4">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">// Recent sends</div>
            {(stats.recent_pushes || []).length === 0 && <div className="text-xs text-white/40">No broadcasts yet.</div>}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {(stats.recent_pushes || []).slice(0, 8).map((p) => (
                <div key={p._id} className="text-xs border-b border-white/8 pb-1.5">
                  <div className="font-semibold truncate">{p.payload?.title || "—"}</div>
                  <div className="text-[10px] text-white/50 mt-0.5">
                    {new Date(p.sent_at).toLocaleString()} · <span className="text-green-400">{p.sent} sent</span>
                    {p.removed > 0 && <> · <span className="text-yellow-400">{p.removed} pruned</span></>}
                    {p.roles && <> · <span className="text-white/40 uppercase">{Array.isArray(p.roles) ? p.roles.join(",") : p.roles}</span></>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="border border-white/12 bg-[#131A2B] p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">{label}</div>
      <div className="font-display text-3xl font-black brand-orange mt-1 leading-none">{value}</div>
      <div className="text-[10px] text-white/40 mt-1">{sub}</div>
    </div>
  );
}
